import React, { useState, useEffect } from 'react';
import C from '../../data/colors';
import { getMessages, getMessageStats, updateMessage, deleteMessage } from '../../data/api';
import { Flag, Check, Star, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const WebCommentsTab = ({ onStatsUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [editId, setEditId] = useState(null);
  const [editContent, setEditContent] = useState('');

  const load = async () => {
    try {
      const f = filter === 'all' ? null : filter;
      const m = await getMessages(f);
      setMessages(m);
      const s = await getMessageStats();
      setStats(s);
      onStatsUpdate?.();
    } catch(e) { console.error(e); }
  };

  useEffect(() => { load(); }, [filter]);

  const toggleStatus = async (id, current) => {
    await updateMessage(id, { status: current==='unread'?'read':'unread' });
    load();
  };
  const toggleFlag = async (id, current) => { await updateMessage(id, { flagged: !current }); load(); };
  const toggleHighlight = async (id, current) => { await updateMessage(id, { highlighted: !current }); load(); };
  const handleDelete = async (id) => { await deleteMessage(id); load(); };
  const startEdit = (m) => { setEditId(m.id); setEditContent(m.content); };
  const saveEdit = async () => {
    if (editId) {
      try {
        await updateMessage(editId, { content: editContent });
        toast.success('Comment updated');
        setEditId(null);
        load();
      } catch (e) {
        toast.error('Failed to update comment');
      }
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>New Web Comments</h2>
        <button onClick={load} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-9-9 9 9 0 0 1 6.36 2.64"/><polyline points="21 3 21 9 15 9"/></svg></button>
      </div>

      {/* Filter with counts */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[['all', stats.total || 0], ['unread', stats.unread || 0], ['read', (stats.total || 0) - (stats.unread || 0)]].map(([f, count]) => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${filter === f ? C.gold : C.border}`, background: filter === f ? C.goldMuted : C.bgCard, color: filter === f ? C.gold : C.textSecondary, fontSize: 12, fontWeight: filter === f ? 600 : 400, cursor: 'pointer', textTransform: 'capitalize' }}>{f} ({count})</button>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 13, color: C.textSecondary }}>
        <span>Total: <strong style={{ color: C.textPrimary }}>{stats.total || 0}</strong></span>
        <span>Notes: <strong style={{ color: C.textPrimary }}>{stats.notes || 0}</strong></span>
        <span>Questions: <strong style={{ color: C.textPrimary }}>{stats.questions || 0}</strong></span>
      </div>

      {/* Messages */}
      {messages.length === 0 ? <div style={{ textAlign: 'center', padding: 32, color: C.textSecondary }}>No messages found.</div> :
        messages.map(m => (
          <div key={m.id} style={{ background: m.status === 'unread' ? C.goldMuted : C.bgCard, border: `1px solid ${m.status === 'unread' ? C.gold : C.border}`, borderRadius: 12, padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {m.status === 'unread' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.gold }} />}
                <span style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>{m.guest_name}</span>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: m.message_type === 'note' ? 'rgba(34,197,94,0.15)' : 'rgba(59,130,246,0.15)', color: m.message_type === 'note' ? '#22c55e' : '#60a5fa' }}>{m.message_type}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => toggleStatus(m.id, m.status)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.status === 'read' ? '#22c55e' : C.textSecondary }} title={m.status === 'unread' ? 'Mark read' : 'Mark unread'}>{m.status === 'read' ? <Eye size={14}/> : <EyeOff size={14}/>}</button>
                <button onClick={() => toggleFlag(m.id, m.flagged)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.flagged ? '#f97316' : C.textSecondary }}><Flag size={14}/></button>
                <button onClick={() => startEdit(m)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#60a5fa' }} title="Edit"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                <button onClick={() => toggleHighlight(m.id, m.highlighted)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.highlighted ? C.gold : C.textSecondary }}><Star size={14}/></button>
                <button onClick={() => handleDelete(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary }} onMouseEnter={e => e.currentTarget.style.color = C.error} onMouseLeave={e => e.currentTarget.style.color = C.textSecondary}><Trash2 size={14}/></button>
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.textPrimary, lineHeight: 1.6, marginBottom: 6 }}>
              {editId === m.id ? (
                <div>
                  <textarea value={editContent} onChange={e => setEditContent(e.target.value)} style={{ width: '100%', minHeight: 60, padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box', resize: 'vertical', background: C.bgInput, color: C.textPrimary }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button onClick={saveEdit} style={{ padding: '4px 14px', borderRadius: 6, background: C.gold, color: '#000', border: 'none', fontSize: 12, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => setEditId(null)} style={{ padding: '4px 14px', borderRadius: 6, background: C.bgPrimary, color: C.textSecondary, border: 'none', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : m.content}
            </div>
            <div style={{ fontSize: 11, color: C.textSecondary }}>{m.email && `${m.email} \u2022 `}{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
    </div>
  );
};

export default WebCommentsTab;
